import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Separator } from '../../components/ui/separator';
import { Textarea } from '../../components/ui/textarea';
import { Slider } from '../../components/ui/slider';
import { 
  Users, Bot, Settings, Shield, Phone, PhoneIncoming, Clock, 
  RefreshCw, Save, CheckCircle, AlertCircle, XCircle, ChevronRight,
  UserCheck, UserX, Headphones, Activity, Target, Calendar,
  Lock, Edit2, Trash2, Plus, Search, Filter, Eye, MoreHorizontal,
  Zap, Play, AlertTriangle, History, ArrowRight, Layers
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ==========================================
// HUMAN AGENTS TAB
// ==========================================
const HumanAgentsTab = ({ hubConfig }) => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);

  const fetchAgents = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/call-center/hub-config/human-agents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgents(response.data.agents || []);
    } catch (err) {
      toast.error('Failed to load agents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const updateAgentSettings = async (userId, settings) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.patch(`${API}/call-center/hub-config/human-agents/${userId}/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Agent settings updated');
      fetchAgents();
    } catch (err) {
      toast.error('Failed to update settings');
    }
  };

  const forceAvailable = async (userId) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${API}/call-center/hub-config/human-agents/${userId}/force-available`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Agent forced to available');
      fetchAgents();
    } catch (err) {
      toast.error('Failed to force availability');
    }
  };

  const getStatusBadge = (agent) => {
    if (agent.is_on_call) {
      return <Badge className="bg-green-500 text-white">On Call</Badge>;
    }
    const status = agent.presence?.status || 'offline';
    const colors = {
      available: 'bg-green-100 text-green-800',
      away: 'bg-yellow-100 text-yellow-800',
      busy: 'bg-orange-100 text-orange-800',
      offline: 'bg-gray-100 text-gray-800'
    };
    return <Badge className={colors[status] || colors.offline}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium">Human Call Center Agents</h3>
          <p className="text-sm text-muted-foreground">Manage agent eligibility, queue assignments, and status</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchAgents}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="grid gap-4">
        {agents.map(agent => (
          <Card key={agent.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                {/* Agent Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-muted-foreground" />
                    <span className="font-medium">{agent.first_name} {agent.last_name}</span>
                    {getStatusBadge(agent)}
                    {agent.presence?.workstation_open && (
                      <Badge variant="outline" className="text-xs">Work Station Open</Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{agent.email}</p>
                </div>

                {/* Stats */}
                <div className="text-center px-4 border-l">
                  <div className="text-lg font-semibold">{agent.calls_today || 0}</div>
                  <div className="text-xs text-muted-foreground">Calls Today</div>
                </div>

                {/* Eligibility Toggles */}
                <div className="flex items-center gap-4 px-4 border-l">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={agent.settings?.discovery_eligible !== false}
                      onCheckedChange={(checked) => updateAgentSettings(agent.id, { discovery_eligible: checked })}
                    />
                    <span className="text-xs">Discovery</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={agent.settings?.demo_eligible !== false}
                      onCheckedChange={(checked) => updateAgentSettings(agent.id, { demo_eligible: checked })}
                    />
                    <span className="text-xs">Demo</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={agent.settings?.ops_support_eligible !== false}
                      onCheckedChange={(checked) => updateAgentSettings(agent.id, { ops_support_eligible: checked })}
                    />
                    <span className="text-xs">Ops</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => forceAvailable(agent.id)}
                  >
                    Force Available
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setSelectedAgent(agent)}>
                    <Edit2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {agents.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No call center agents found</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ==========================================
// AI AGENTS TAB
// ==========================================
const AIAgentsTab = ({ hubConfig }) => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [testMessage, setTestMessage] = useState('');
  const [testResponse, setTestResponse] = useState(null);
  const [isTesting, setIsTesting] = useState(false);

  const fetchAgents = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/call-center/ai-answer/agents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgents(response.data.agents || []);
    } catch (err) {
      toast.error('Failed to load AI agents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const toggleAgent = async (agentId, enabled) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.patch(`${API}/call-center/ai-answer/agents/${agentId}/status?enabled=${enabled}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Agent ${enabled ? 'enabled' : 'disabled'}`);
      fetchAgents();
    } catch (err) {
      toast.error('Failed to update agent');
    }
  };

  const updateAgent = async (agentId, data) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.patch(`${API}/call-center/ai-answer/agents/${agentId}`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Agent updated');
      fetchAgents();
      setSelectedAgent(null);
    } catch (err) {
      toast.error('Failed to update agent');
    }
  };

  const testAgent = async () => {
    if (!testMessage.trim() || !selectedAgent) return;
    setIsTesting(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(`${API}/call-center/ai-answer/agents/${selectedAgent.id}/test`, 
        { test_message: testMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTestResponse(response.data);
    } catch (err) {
      toast.error('Test failed');
    } finally {
      setIsTesting(false);
    }
  };

  const specialtyConfig = {
    general_inbound: { label: 'General', color: 'bg-blue-100 text-blue-800' },
    discovery_screening: { label: 'Discovery', color: 'bg-purple-100 text-purple-800' },
    appointment_scheduling: { label: 'Scheduling', color: 'bg-green-100 text-green-800' },
    sales_demo: { label: 'Sales Demo', color: 'bg-yellow-100 text-yellow-800' },
    operations_support: { label: 'Ops Support', color: 'bg-orange-100 text-orange-800' },
    overflow_backup: { label: 'Overflow', color: 'bg-gray-100 text-gray-800' },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium">AI Answer Agents</h3>
          <p className="text-sm text-muted-foreground">15 backup AI agents for overflow call handling</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline">{agents.filter(a => a.is_enabled).length} Enabled</Badge>
          <Button variant="outline" size="sm" onClick={fetchAgents}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {agents.map(agent => {
          const specialty = specialtyConfig[agent.specialty] || specialtyConfig.general_inbound;
          return (
            <Card 
              key={agent.id} 
              className={`cursor-pointer transition-all hover:shadow-md ${!agent.is_enabled ? 'opacity-60' : ''}`}
              onClick={() => setSelectedAgent(agent)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Bot className="h-5 w-5 text-cyan-500" />
                    <span className="font-medium text-sm">{agent.name}</span>
                  </div>
                  <Switch
                    checked={agent.is_enabled}
                    onCheckedChange={(checked) => {
                      toggleAgent(agent.id, checked);
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={`text-xs ${specialty.color}`}>{specialty.label}</Badge>
                  {agent.current_session_id && (
                    <Badge className="bg-green-500 text-white text-xs">On Call</Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  &quot;{agent.display_name}&quot; • {agent.sessions_today || 0} today
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* AI Agent Training Dialog */}
      <Dialog open={!!selectedAgent} onOpenChange={() => { setSelectedAgent(null); setTestResponse(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-cyan-500" />
              {selectedAgent?.name} - Training & Configuration
            </DialogTitle>
            <DialogDescription>
              Configure prompts, escalation rules, and test the agent
            </DialogDescription>
          </DialogHeader>

          {selectedAgent && (
            <div className="space-y-6">
              {/* Identity */}
              <div className="space-y-3">
                <Label>Display Name (what caller hears)</Label>
                <Input
                  defaultValue={selectedAgent.display_name}
                  onBlur={(e) => updateAgent(selectedAgent.id, { display_name: e.target.value })}
                />
              </div>

              {/* Greeting Prompt */}
              <div className="space-y-3">
                <Label>Greeting Prompt</Label>
                <Textarea
                  defaultValue={selectedAgent.greeting_prompt}
                  rows={3}
                  onBlur={(e) => updateAgent(selectedAgent.id, { greeting_prompt: e.target.value })}
                />
              </div>

              {/* Screening Prompt */}
              <div className="space-y-3">
                <Label>Screening Prompt (Intent Detection)</Label>
                <Textarea
                  defaultValue={selectedAgent.screening_prompt}
                  rows={3}
                  onBlur={(e) => updateAgent(selectedAgent.id, { screening_prompt: e.target.value })}
                />
              </div>

              {/* Escalation Rules */}
              <div className="space-y-3">
                <Label>Escalation Keywords</Label>
                <p className="text-xs text-muted-foreground">Immediately escalate to human if caller says these words</p>
                <div className="flex flex-wrap gap-1">
                  {selectedAgent.escalation_keywords?.map((kw, i) => (
                    <Badge key={i} variant="secondary">{kw}</Badge>
                  ))}
                </div>
              </div>

              <Separator />

              {/* Test Simulator */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Play className="h-4 w-4" />
                  Test Simulator
                </Label>
                <Textarea
                  placeholder="Enter a simulated caller message..."
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  rows={2}
                />
                <Button onClick={testAgent} disabled={isTesting || !testMessage.trim()}>
                  {isTesting ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
                  Test Response
                </Button>

                {testResponse && (
                  <Card className="bg-muted/50">
                    <CardContent className="p-3 space-y-2">
                      <p className="text-sm"><strong>Response:</strong> {testResponse.response}</p>
                      <div className="flex gap-2">
                        <Badge variant="outline">Intent: {testResponse.intent_detected}</Badge>
                        <Badge variant="outline">Action: {testResponse.suggested_action}</Badge>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==========================================
// QUEUES TAB
// ==========================================
const QueuesTab = ({ hubConfig }) => {
  const [queues, setQueues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingQueue, setEditingQueue] = useState(null);

  const fetchQueues = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/call-center/hub-config/queues`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQueues(response.data.queues || []);
    } catch (err) {
      toast.error('Failed to load queues');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQueues();
  }, [fetchQueues]);

  const seedQueues = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${API}/call-center/hub-config/queues/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Default queues created');
      fetchQueues();
    } catch (err) {
      toast.error('Failed to seed queues');
    }
  };

  const updateQueue = async (queueId, data) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.patch(`${API}/call-center/hub-config/queues/${queueId}`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Queue updated');
      fetchQueues();
    } catch (err) {
      toast.error('Failed to update queue');
    }
  };

  const queueTypeConfig = {
    discovery: { label: 'Discovery', color: 'bg-purple-100 text-purple-800' },
    sales_demo: { label: 'Sales Demo', color: 'bg-yellow-100 text-yellow-800' },
    ops_support: { label: 'Ops Support', color: 'bg-orange-100 text-orange-800' },
    general: { label: 'General', color: 'bg-blue-100 text-blue-800' },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium">Call Queues</h3>
          <p className="text-sm text-muted-foreground">Configure how calls flow through your system</p>
        </div>
        <div className="flex items-center gap-2">
          {queues.length === 0 && (
            <Button variant="outline" onClick={seedQueues}>
              <Plus className="h-4 w-4 mr-2" />
              Create Default Queues
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={fetchQueues}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid gap-4">
        {queues.map(queue => {
          const qType = queueTypeConfig[queue.queue_type] || queueTypeConfig.general;
          return (
            <Card key={queue.id}>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Layers className="h-5 w-5 text-muted-foreground" />
                      <span className="font-medium">{queue.name}</span>
                      <Badge className={`text-xs ${qType.color}`}>{qType.label}</Badge>
                      {!queue.is_active && <Badge variant="outline" className="text-xs">Inactive</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{queue.description || 'No description'}</p>
                  </div>

                  {/* Live Stats */}
                  <div className="grid grid-cols-3 gap-4 text-center border-l pl-4">
                    <div>
                      <div className="text-lg font-semibold">{queue.current_waiting || 0}</div>
                      <div className="text-xs text-muted-foreground">Waiting</div>
                    </div>
                    <div>
                      <div className="text-lg font-semibold">{queue.staffed_human || 0}</div>
                      <div className="text-xs text-muted-foreground">Human</div>
                    </div>
                    <div>
                      <div className="text-lg font-semibold">{queue.staffed_ai || 0}</div>
                      <div className="text-xs text-muted-foreground">AI</div>
                    </div>
                  </div>

                  {/* Settings */}
                  <div className="flex items-center gap-4 border-l pl-4">
                    <div className="text-sm">
                      <span className="text-muted-foreground">Routing:</span> {queue.routing_strategy}
                    </div>
                    <div className="text-sm">
                      <span className="text-muted-foreground">Overflow:</span> {queue.overflow_target}
                    </div>
                  </div>

                  {/* Toggle & Edit */}
                  <div className="flex items-center gap-2 border-l pl-4">
                    <Switch
                      checked={queue.is_active}
                      onCheckedChange={(checked) => updateQueue(queue.id, { is_active: checked })}
                    />
                    <Button variant="ghost" size="icon" onClick={() => setEditingQueue(queue)}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}

        {queues.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <Layers className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No queues configured</p>
            <p className="text-sm mt-1">Click &quot;Create Default Queues&quot; to get started</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ==========================================
// SETTINGS TAB
// ==========================================
const SettingsTab = ({ hubConfig, onConfigUpdate }) => {
  const [config, setConfig] = useState(hubConfig || {});
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setConfig(hubConfig || {});
  }, [hubConfig]);

  const updateField = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.patch(`${API}/call-center/hub-config/config`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings saved');
      setHasChanges(false);
      onConfigUpdate?.();
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${API}/call-center/hub-config/config/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings reset to defaults');
      onConfigUpdate?.();
    } catch (err) {
      toast.error('Failed to reset settings');
    }
  };

  return (
    <div className="space-y-6">
      {/* Sticky Save Bar */}
      {hasChanges && (
        <div className="sticky top-0 z-10 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            <span className="text-sm font-medium">You have unsaved changes</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setConfig(hubConfig)}>
              Discard
            </Button>
            <Button size="sm" onClick={saveConfig} disabled={saving}>
              {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </div>
      )}

      {/* Discovery Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Discovery Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Slot Duration (minutes)</Label>
              <Input
                type="number"
                value={config.discovery_slot_duration || 30}
                onChange={(e) => updateField('discovery_slot_duration', parseInt(e.target.value))}
              />
            </div>
            <div className="space-y-2">
              <Label>Booking Cutoff (minutes before slot)</Label>
              <Input
                type="number"
                value={config.booking_cutoff_minutes || 30}
                onChange={(e) => updateField('booking_cutoff_minutes', parseInt(e.target.value))}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Call Handling Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5" />
            Call Handling
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Max Hold Before Callback (seconds)</Label>
              <Input
                type="number"
                value={config.max_hold_seconds || 90}
                onChange={(e) => updateField('max_hold_seconds', parseInt(e.target.value))}
              />
            </div>
            <div className="space-y-2">
              <Label>Wrap-up Duration (seconds)</Label>
              <Input
                type="number"
                value={config.wrap_up_seconds || 60}
                onChange={(e) => updateField('wrap_up_seconds', parseInt(e.target.value))}
              />
            </div>
            <div className="space-y-2">
              <Label>Max Ring Time (seconds)</Label>
              <Input
                type="number"
                value={config.max_ring_seconds || 30}
                onChange={(e) => updateField('max_ring_seconds', parseInt(e.target.value))}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Call Next Strategy</Label>
            <Select
              value={config.call_next_strategy || 'priority'}
              onValueChange={(value) => updateField('call_next_strategy', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="priority">Priority-based</SelectItem>
                <SelectItem value="fifo">First In, First Out</SelectItem>
                <SelectItem value="round_robin">Round Robin</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Notification Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Notifications
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Booking Confirmation</Label>
              <Switch
                checked={config.notify_booking_confirmation !== false}
                onCheckedChange={(checked) => updateField('notify_booking_confirmation', checked)}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>15-minute Reminder</Label>
              <Switch
                checked={config.notify_15min_reminder !== false}
                onCheckedChange={(checked) => updateField('notify_15min_reminder', checked)}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Missed Call Task Creation</Label>
              <Switch
                checked={config.notify_missed_call_task !== false}
                onCheckedChange={(checked) => updateField('notify_missed_call_task', checked)}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Callback Due Notification</Label>
              <Switch
                checked={config.notify_callback_due !== false}
                onCheckedChange={(checked) => updateField('notify_callback_due', checked)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* AI Overflow Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            AI Overflow
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Enable AI Overflow</Label>
              <p className="text-xs text-muted-foreground">Route to AI agents when no humans available</p>
            </div>
            <Switch
              checked={config.ai_overflow_enabled !== false}
              onCheckedChange={(checked) => updateField('ai_overflow_enabled', checked)}
            />
          </div>
          <div className="space-y-2">
            <Label>Overflow After (seconds)</Label>
            <Input
              type="number"
              value={config.ai_overflow_after_seconds || 30}
              onChange={(e) => updateField('ai_overflow_after_seconds', parseInt(e.target.value))}
              disabled={!config.ai_overflow_enabled}
            />
          </div>
        </CardContent>
      </Card>

      {/* Reset Button */}
      <div className="flex justify-end">
        <Button variant="outline" onClick={resetToDefaults}>
          Reset to Defaults
        </Button>
      </div>
    </div>
  );
};

// ==========================================
// MAIN COMPONENT
// ==========================================
export default function CallCenterSettings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [hubConfig, setHubConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  const activeTab = searchParams.get('tab') || 'agents';
  const agentSubTab = searchParams.get('type') || 'human';

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  const isManager = user?.role === 'call_center_manager';
  const hasAccess = isAdmin || isManager;

  const fetchConfig = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/call-center/hub-config/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHubConfig(response.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Access denied');
        navigate('/call-center/control-panel');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Check if settings are disabled
  if (!loading && hubConfig && !hubConfig.enable_settings_ui && !isAdmin) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[80vh]">
          <Card className="max-w-md">
            <CardContent className="p-6 text-center">
              <Lock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Settings Disabled</h2>
              <p className="text-muted-foreground">
                Call Center settings have been disabled by an administrator.
              </p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  if (!hasAccess) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[80vh]">
          <Card className="max-w-md">
            <CardContent className="p-6 text-center">
              <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Access Restricted</h2>
              <p className="text-muted-foreground">
                Only administrators and call center managers can access these settings.
              </p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="h-[calc(100vh-4rem)] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Settings className="h-6 w-6" />
                Call Center Settings
              </h1>
              <p className="text-muted-foreground">
                Manage agents, queues, and operational settings
              </p>
            </div>
            <Button variant="outline" onClick={fetchConfig}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Main Tabs */}
          <Tabs value={activeTab} onValueChange={(v) => setSearchParams({ tab: v })} className="mt-4">
            <TabsList>
              <TabsTrigger value="agents" className="gap-2">
                <Users className="h-4 w-4" />
                Agents
              </TabsTrigger>
              <TabsTrigger value="queues" className="gap-2">
                <Layers className="h-4 w-4" />
                Queues
              </TabsTrigger>
              <TabsTrigger value="settings" className="gap-2">
                <Settings className="h-4 w-4" />
                Settings
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {activeTab === 'agents' && (
                <div className="space-y-4">
                  {/* Agent Sub-tabs */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant={agentSubTab === 'human' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSearchParams({ tab: 'agents', type: 'human' })}
                    >
                      <Users className="h-4 w-4 mr-2" />
                      Human Agents
                    </Button>
                    <Button
                      variant={agentSubTab === 'ai' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSearchParams({ tab: 'agents', type: 'ai' })}
                    >
                      <Bot className="h-4 w-4 mr-2" />
                      AI Answer Agents
                    </Button>
                  </div>

                  {agentSubTab === 'human' && <HumanAgentsTab hubConfig={hubConfig} />}
                  {agentSubTab === 'ai' && <AIAgentsTab hubConfig={hubConfig} />}
                </div>
              )}

              {activeTab === 'queues' && <QueuesTab hubConfig={hubConfig} />}
              {activeTab === 'settings' && <SettingsTab hubConfig={hubConfig} onConfigUpdate={fetchConfig} />}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
